/** One dispatched Server-Sent Event, exactly as it came off the wire. */
export interface SseMessage {
    /** `event:` field, or "message" when the stream omits it. */
    event: string;
    /** `data:` field — multi-line data is joined with "\n", per the SSE spec. */
    data: string;
    /** `id:` field, if the server sent one. */
    id: string | null;
}

/**
 * An event whose `data` has been parsed as JSON.
 *
 * `data` is `null` when the frame was not valid JSON; `raw` always holds the
 * original string, so a plain-text stream is still usable.
 */
export interface SseEvent<T> {
    /** `event:` name, or "message" when the stream omits it. */
    event: string;
    /** `id:` field, if the server sent one. */
    id: string | null;
    /** Parsed payload, or null when `raw` was not JSON. */
    data: T | null;
    /** The unparsed `data:` string. */
    raw: string;
}

export interface SseOptions {
    /**
     * How many times to reconnect after a drop before giving up.
     * 0 disables reconnection. Defaults to 3.
     */
    maxRetries?: number;
    /** Base backoff in ms; doubles per attempt, capped at 30s. Defaults to 1000. */
    retryDelayMs?: number;
    /** Send the app's Authorization header. Defaults to true. */
    withAuth?: boolean;
    /**
     * Only emit these `event:` names. Omit to receive everything.
     * Comment/keep-alive frames never reach you either way.
     */
    events?: string[];
}

/** Options for a stream that ends on its own terminal event. */
export interface SseUntilOptions<T> extends SseOptions {
    /**
     * True when this event ends the stream successfully. The event is emitted,
     * then the observable completes and the connection is closed.
     */
    isComplete: (event: SseEvent<T>) => boolean;
    /**
     * True when this event ends the stream in failure. The observable errors
     * with an {@link SseStreamError} and the connection is closed.
     * Optional — omit if the stream has no failure event.
     */
    isFailure?: (event: SseEvent<T>) => boolean;
}

/** Raised when a stream reports failure through its own protocol. */
export class SseStreamError<T = unknown> extends Error {
    constructor(readonly event: SseEvent<T>, message?: string) {
        super(message || `SSE stream reported failure on event "${event.event}"`);
        // Required when targeting ES5 so `instanceof` keeps working.
        Object.setPrototypeOf(this, SseStreamError.prototype);
        this.name = "SseStreamError";
    }
}
